import { Collapse, CollapsePanelProps, List, Modal, notification } from "antd";
import { FC, VFC } from "react";
import { Member } from "../../../utils/Game";
import { useSelector } from "../../../../../redux/store";
import { ArrowRightOutlined } from "@ant-design/icons";
import styled, { css } from "styled-components";
import { ActionMenu } from "./ActionMenu";
import { useRemovePlayerMutation } from "../../../utils/useRemovePlayerMutation";
import { memberRankingSelector } from "../../../../../redux/selectors/memberRankingSelector";

export const MemberPanel: FC<
  CollapsePanelProps & {
    members: Member[];
    isCurrentUserGameMaster: boolean;
  }
> = ({ members, isCurrentUserGameMaster, ...props }) => {
  return (
    <StyledPanel {...props}>
      <MemberList
        isCurrentUserGameMaster={isCurrentUserGameMaster}
        members={members}
      />
    </StyledPanel>
  );
};

export const MemberList: VFC<{
  members: Member[];
  isCurrentUserGameMaster: boolean;
}> = ({ members, isCurrentUserGameMaster }) => {
  const game = useSelector((state) => state.game.game);
  const currentUser = useSelector((state) => state.auth.user);

  const removePlayerMutation = useRemovePlayerMutation();
  return (
    <List>
      {members.map((member) => {
        const isMe = currentUser?.uid === member.userId;
        const isGameMaster = game?.masterUserId === member.userId;

        const hideInventedName = isMe && !member.solved;
        return (
          <StyledItem
            // @ts-ignore
            $isMe={isMe}
            key={member.userId}
            actions={[
              <ActionMenu
                isMe={isMe}
                onRemovePlayer={async () => {
                  Modal.confirm({
                    title: `Den Spieler "${member.displayName}" entfernen?`,
                    okText: "Entfernen",
                    cancelText: "Abbrechen",
                    content:
                      "Der Spieler ist nicht mehr dabei? Dann kannst du Ihn entfernen. Der Spieler kann aber jederzeit wieder dazukommen.",
                    onOk: async () => {
                      await removePlayerMutation.mutate(member.userId);

                      notification.success({
                        message: `${member.displayName} wurde entfernt`,
                        description: `Der Spieler mit dem Namen ${member.displayName} wurde entfernt.`,
                      });
                    },
                  });
                }}
                isCurrentUserGameMaster={isCurrentUserGameMaster}
              />,
            ]}
          >
            <List.Item.Meta
              avatar={
                <StatusAvatar
                  userId={member.userId}
                  solved={member.solved ?? false}
                  active={game?.activePlayerId === member.userId}
                />
              }
              title={
                <span>
                  {member.displayName} {isGameMaster && " - Spielleiter"}
                </span>
              }
              description={
                member.inventedName ? (
                  <InventedName $hide={hideInventedName}>
                    {hideInventedName ? "??????????????" : member.inventedName}
                  </InventedName>
                ) : (
                  <span>&nbsp;</span>
                )
              }
            />
          </StyledItem>
        );
      })}
    </List>
  );
};

export const InventedName = styled.span<{ $hide: boolean }>`
  padding: 0px;
  ${(props) =>
    props.$hide &&
    css`
      filter: blur(3px);
    `}
`;

const StatusAvatar: VFC<{
  solved: boolean;
  active: boolean;
  userId: string;
}> = ({ solved, active, userId }) => {
  const rankingList = useSelector(memberRankingSelector);

  if (solved) {
    const rankingIndex = rankingList.findIndex((item) => item === userId);
    const ranking = rankingIndex + 1;
    return <RankingSpan>{ranking}.</RankingSpan>;
  }

  if (active) {
    return <ArrowRightOutlined style={{ fontSize: 40 }} />;
  }

  return <div style={{ height: 40, width: 40 }}></div>;
};

const StyledItem = styled(List.Item)<{ $isMe: boolean }>`
  ${(props) =>
    props.$isMe &&
    css`
      background-color: var(--color-green-75);
    `}
  padding: 8px;
` as typeof List.Item;

const RankingSpan = styled.span`
  font-size: 40px;
  font-weight: 500;
`;

export const StyledPanel = styled(Collapse.Panel)`
  .ant-collapse-header {
    background-color: white;
  }
  .ant-collapse-content > .ant-collapse-content-box {
    padding: 0px;
  }
`;
